import maplibregl, { type GeoJSONSource, type Map as MapLibreMap, type Marker } from "maplibre-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  categoryLabels, emergencyGuides, hospitalityLinks,
  safetySources, stages, trainRules,
  type OsmResource, type ResourceCategory, type Stage,
} from "./data";

type Tab = "today" | "map" | "stages" | "resources" | "safety" | "team";
type Position = { lat: number; lon: number; accuracy: number };
type RoutePoint = { lat: number; lon: number; ele: number };
type RouteData = {
  points: RoutePoint[];
  stageBreaks: number[];
  stageBoundaryPoints: RoutePoint[];
  pointCount: number;
};
type ResourcePayload = { generatedAt: string; license: string; count: number; resources: OsmResource[] };
type Traveler = {
  id: string;
  name: string;
  monogram: string;
  status: "documented" | "incomplete";
  tagline: string;
  bike: { name: string; url: string | null } | null;
  shelter: { name: string; url: string | null } | null;
  gear: { name: string; category: string; url: string | null }[];
  notes: string[];
};
type LocalState = {
  favorites: string[];
  resourceStates: Record<string, string>;
  notes: Record<string, string>;
  selectedStage: number;
  theme: "light" | "dark";
  lastSafetyCheck: string | null;
};
type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const initialLocal: LocalState = {
  favorites: [], resourceStates: {}, notes: {},
  selectedStage: 1, theme: "light", lastSafetyCheck: null,
};

const navItems: { id: Tab; label: string; mark: string }[] = [
  { id: "today", label: "Aujourd’hui", mark: "J" },
  { id: "map", label: "Carte", mark: "⌁" },
  { id: "stages", label: "Étapes", mark: "13" },
  { id: "resources", label: "Ressources", mark: "+" },
  { id: "safety", label: "Sécurité", mark: "!" },
];

function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const radius = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLon = (b.lon - a.lon) * Math.PI / 180;
  const lat1 = a.lat * Math.PI / 180;
  const lat2 = b.lat * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * radius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function formatDistance(value: number) {
  if (value < 1) return `${Math.max(10, Math.round(value * 1000 / 10) * 10)} m`;
  return `${value.toFixed(value < 10 ? 1 : 0)} km`;
}

function whenLabel(iso: string | null) {
  if (!iso) return "Jamais vérifié";
  const date = new Date(iso);
  return `Vérifié ${date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} à ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function CompanionClient() {
  const [tab, setTab] = useState<Tab>("today");
  const [local, setLocal] = useState<LocalState>(initialLocal);
  const [route, setRoute] = useState<RouteData | null>(null);
  const [resources, setResources] = useState<OsmResource[]>([]);
  const [resourceMeta, setResourceMeta] = useState({ generatedAt: "", count: 0 });
  const [travelers, setTravelers] = useState<Traveler[]>([]);
  const [position, setPosition] = useState<Position | null>(null);
  const [gpsMessage, setGpsMessage] = useState("Position non activée");
  const [online, setOnline] = useState(true);
  const [offlineReady, setOfflineReady] = useState(false);
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(null);
  const [selectedResource, setSelectedResource] = useState<OsmResource | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ResourceCategory[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [safetyPane, setSafetyPane] = useState<"risks" | "trains" | "sleep" | "emergency">("risks");
  const watchRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("velosud-state-v1");
      if (saved) setLocal({ ...initialLocal, ...JSON.parse(saved) });
    } catch {}
    setOnline(navigator.onLine);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    fetch("/route.json").then((response) => response.json()).then(setRoute).catch(() => {});
    fetch("/osm-resources.json").then((response) => response.json()).then((data: ResourcePayload) => {
      setResources(data.resources);
      setResourceMeta({ generatedAt: data.generatedAt, count: data.count });
    }).catch(() => {});
    fetch("/data/travelers.json").then((response) => response.json()).then((data: { travelers: Traveler[] }) => setTravelers(data.travelers)).catch(() => {});
    const onOfflineReady = () => {
      setOfflineBusy(false);
      setOfflineReady(true);
      localStorage.setItem("velosud-offline-ready", "yes");
    };
    window.addEventListener("velosud:offline-ready", onOfflineReady);
    setOfflineReady(localStorage.getItem("velosud-offline-ready") === "yes");
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(async () => {
        const [routeCached, resourcesCached] = await Promise.all([
          caches.match("/route.json"),
          caches.match("/osm-resources.json"),
        ]);
        if (routeCached && resourcesCached) onOfflineReady();
      }).catch(() => {});
    }
    const onInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("velosud:offline-ready", onOfflineReady);
      window.removeEventListener("beforeinstallprompt", onInstall);
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = local.theme;
    localStorage.setItem("velosud-state-v1", JSON.stringify(local));
  }, [local]);

  const inferredStage = useMemo(() => {
    if (!position || !route) return local.selectedStage;
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    route.points.forEach((point, index) => {
      const distance = distanceKm(position, point);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index * 16;
      }
    });
    const boundary = route.stageBreaks.findIndex((value) => nearestIndex < value);
    return Math.min(13, Math.max(1, boundary < 1 ? 1 : boundary));
  }, [position, route, local.selectedStage]);

  const activeStage = stages[inferredStage - 1];

  const nearestResources = useMemo(() => {
    const pool = resources.filter((resource) => resource.stageId === inferredStage);
    return pool
      .map((resource) => ({
        resource,
        distance: position ? distanceKm(position, resource) : resource.distanceToRouteKm,
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 8);
  }, [resources, inferredStage, position]);

  const visibleResources = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("fr");
    return resources
      .filter((resource) => !showFavorites || local.favorites.includes(resource.id))
      .filter((resource) => filters.length === 0 || filters.includes(resource.category))
      .filter((resource) => local.selectedStage === 0 || resource.stageId === local.selectedStage)
      .filter((resource) => !needle || `${resource.name} ${categoryLabels[resource.category].label}`.toLocaleLowerCase("fr").includes(needle))
      .map((resource) => ({
        resource,
        distance: position ? distanceKm(position, resource) : resource.distanceToRouteKm,
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 150);
  }, [resources, search, filters, local.selectedStage, showFavorites, local.favorites, position]);

  function updateLocal(patch: Partial<LocalState>) {
    setLocal((current) => ({ ...current, ...patch }));
  }

  function toggleFavorite(id: string) {
    updateLocal({
      favorites: local.favorites.includes(id)
        ? local.favorites.filter((favorite) => favorite !== id)
        : [...local.favorites, id],
    });
  }

  function locate() {
    if (!navigator.geolocation) {
      setGpsMessage("GPS indisponible sur ce téléphone");
      return;
    }
    setGpsMessage("Recherche de la position…");
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => {
        setPosition({ lat: coords.latitude, lon: coords.longitude, accuracy: coords.accuracy });
        setGpsMessage(`Position à ±${Math.round(coords.accuracy)} m`);
      },
      (error) => setGpsMessage(error.code === 1 ? "Autorisation GPS refusée" : "Position introuvable"),
      { enableHighAccuracy: true, maximumAge: 20000, timeout: 15000 },
    );
  }

  function stopLocate() {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    setPosition(null);
    setGpsMessage("Mode exploration · GPS inactif");
  }

  async function install() {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } else {
      alert("Dans Chrome : menu ⋮ puis « Ajouter à l’écran d’accueil » ou « Installer l’application ».");
    }
  }

  async function prepareOffline() {
    setOfflineBusy(true);
    if (!("serviceWorker" in navigator)) {
      setOfflineBusy(false);
      return;
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      const [routeCached, resourcesCached] = await Promise.all([
        caches.match("/route.json"),
        caches.match("/osm-resources.json"),
      ]);
      const complete = Boolean(routeCached && resourcesCached);
      setOfflineReady(complete);
      localStorage.setItem("velosud-offline-ready", complete ? "yes" : "partial");
    } finally {
      setOfflineBusy(false);
    }
  }

  function markSafetyChecked() {
    updateLocal({ lastSafetyCheck: new Date().toISOString() });
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => setTab("today")} aria-label="Retour à aujourd’hui">
          <span className="brand-route" aria-hidden="true"><i /><i /><i /></span>
          <span><strong>VÉLOSUD</strong><small>compagnon de route</small></span>
        </button>
        <div className="top-actions">
          <span className={`network-dot ${online ? "online" : ""}`} title={online ? "En ligne" : "Hors ligne"} />
          <button className="team-button" onClick={() => setTab("team")} aria-label="Voir Olivier et Hugues">
            <span>O</span><span>H</span>
          </button>
          <button className="icon-button" onClick={() => updateLocal({ theme: local.theme === "light" ? "dark" : "light" })} aria-label="Changer de thème">
            {local.theme === "light" ? "NUIT" : "JOUR"}
          </button>
        </div>
      </header>

      <section className="content">
        {tab === "today" && (
          <TodayView
            stage={activeStage}
            stageNumber={inferredStage}
            gpsMessage={gpsMessage}
            position={position}
            onLocate={locate}
            nearest={nearestResources}
            onResource={setSelectedResource}
            offlineReady={offlineReady}
            offlineBusy={offlineBusy}
            onOffline={prepareOffline}
            onInstall={install}
            lastSafetyCheck={local.lastSafetyCheck}
            onSafety={() => { setTab("safety"); setSafetyPane("risks"); }}
            onStage={() => setSelectedStage(activeStage)}
            onMap={() => setTab("map")}
          />
        )}
        {tab === "map" && (
          <MapView
            route={route}
            resources={resources}
            position={position}
            selectedStage={local.selectedStage || inferredStage}
            onStage={(stageId) => updateLocal({ selectedStage: stageId })}
            filters={filters}
            onFilters={setFilters}
            onResource={setSelectedResource}
            onLocate={locate}
            onStopLocate={stopLocate}
            gpsMessage={gpsMessage}
          />
        )}
        {tab === "stages" && (
          <StagesView
            current={inferredStage}
            selected={local.selectedStage}
            onSelect={(stage) => {
              updateLocal({ selectedStage: stage.id });
              setSelectedStage(stage);
            }}
          />
        )}
        {tab === "resources" && (
          <ResourcesView
            items={visibleResources}
            count={resourceMeta.count}
            generatedAt={resourceMeta.generatedAt}
            search={search}
            onSearch={setSearch}
            filters={filters}
            onFilters={setFilters}
            stage={local.selectedStage}
            onStage={(stageId) => updateLocal({ selectedStage: stageId })}
            favoritesOnly={showFavorites}
            onFavorites={setShowFavorites}
            favoriteIds={local.favorites}
            states={local.resourceStates}
            onSelect={setSelectedResource}
          />
        )}
        {tab === "safety" && (
          <SafetyView
            pane={safetyPane}
            onPane={setSafetyPane}
            lastCheck={local.lastSafetyCheck}
            onChecked={markSafetyChecked}
          />
        )}
        {tab === "team" && <TeamView travelers={travelers} />}
      </section>

      <a className="emergency-fab" href="tel:112" aria-label="Appeler le 112">
        <strong>112</strong><span>URGENCE</span>
      </a>

      <nav className="bottom-nav" aria-label="Navigation principale">
        {navItems.map((item) => (
          <button key={item.id} className={tab === item.id ? "active" : ""} onClick={() => setTab(item.id)}>
            <span>{item.mark}</span>{item.label}
          </button>
        ))}
      </nav>

      {selectedResource && (
        <ResourceSheet
          resource={selectedResource}
          favorite={local.favorites.includes(selectedResource.id)}
          state={local.resourceStates[selectedResource.id] || "à vérifier"}
          note={local.notes[selectedResource.id] || ""}
          onFavorite={() => toggleFavorite(selectedResource.id)}
          onState={(state) => updateLocal({ resourceStates: { ...local.resourceStates, [selectedResource.id]: state } })}
          onNote={(note) => updateLocal({ notes: { ...local.notes, [selectedResource.id]: note } })}
          onClose={() => setSelectedResource(null)}
        />
      )}
      {selectedStage && <StageSheet stage={selectedStage} onClose={() => setSelectedStage(null)} />}
    </main>
  );
}

function TodayView(props: {
  stage: Stage; stageNumber: number; gpsMessage: string; position: Position | null;
  onLocate: () => void; nearest: { resource: OsmResource; distance: number }[];
  onResource: (resource: OsmResource) => void;
  offlineReady: boolean; offlineBusy: boolean; onOffline: () => void; onInstall: () => void;
  lastSafetyCheck: string | null; onSafety: () => void; onStage: () => void; onMap: () => void;
}) {
  return (
    <div className="view today-view">
      <div className="crossing-ribbon">
        <span>ATLANTIQUE</span><i /><i /><i /><i /><i /><b>ÉTAPE {props.stageNumber}</b><i /><i /><i /><span>MÉDITERRANÉE</span>
      </div>
      <section className="hero-stage">
        <div className="hero-kicker">EN ROUTE · {props.stage.checkedAt.split("-").reverse().slice(0, 2).join("/")}</div>
        <button className="hero-title" onClick={props.onStage}>
          <span>{props.stage.from}</span><i>→</i><span>{props.stage.to}</span>
        </button>
        <div className="hero-stats">
          <div><strong>{props.stage.distanceKm}</strong><small>KM</small></div>
          <div><strong>{props.stage.officialTime.replace("≈ ", "")}</strong><small>ROULAGE</small></div>
          <div><strong>{props.stage.difficulty}</strong><small>NIVEAU</small></div>
        </div>
        <p>{props.stage.summary}</p>
        <div className="hero-actions">
          <button className="primary-map-button" onClick={props.onMap}>Ouvrir la carte <span>→</span></button>
          <button className="locate-button" onClick={props.onLocate}>
            <span className={props.position ? "pulse live" : "pulse"} /> {props.gpsMessage}
          </button>
        </div>
      </section>

      <button className={`safety-strip ${props.lastSafetyCheck ? "checked" : ""}`} onClick={props.onSafety}>
        <span className="safety-mark">!</span>
        <span><strong>Feu · météo · arrêtés</strong><small>{whenLabel(props.lastSafetyCheck)}</small></span>
        <b>VÉRIFIER</b>
      </button>

      <section className="section-block">
        <header className="section-heading">
          <div><span>À PORTÉE</span><h2>Ressources proches</h2></div>
          <small>{props.position ? "depuis votre position" : "sur l’étape"}</small>
        </header>
        <div className="nearby-list">
          {props.nearest.length === 0 && <p className="empty">Chargement des données ouvertes…</p>}
          {props.nearest.slice(0, 5).map(({ resource, distance }) => (
            <button key={resource.id} onClick={() => props.onResource(resource)} className="nearby-row">
              <span className={`category-mark cat-${resource.category}`}>{categoryLabels[resource.category].icon}</span>
              <span><strong>{resource.name}</strong><small>{categoryLabels[resource.category].label} · étape {resource.stageId}</small></span>
              <b>{formatDistance(distance)}</b>
            </button>
          ))}
        </div>
      </section>

      <section className="section-block elevation-block">
        <header className="section-heading">
          <div><span>RELIEF DE L’ÉTAPE</span><h2>Profil altimétrique</h2></div>
          <small>{props.stage.distanceKm} km</small>
        </header>
        <ElevationProfile stage={props.stage} />
      </section>

      <section className="offline-card">
        <div className="offline-status">
          <span className={props.offlineReady ? "ready" : ""}>{props.offlineReady ? "✓" : "↓"}</span>
          <div><strong>{props.offlineReady ? "Prêt sans réseau" : "Préparer le hors-ligne"}</strong><small>Trace, 9 000+ ressources, étapes et urgence</small></div>
        </div>
        <div className="offline-actions">
          <button onClick={props.onOffline} disabled={props.offlineBusy}>{props.offlineBusy ? "Téléchargement…" : "Télécharger"}</button>
          <button onClick={props.onInstall}>Installer</button>
        </div>
      </section>
    </div>
  );
}

function ElevationProfile({ stage }: { stage: Stage }) {
  const [profile, setProfile] = useState<{ values: number[]; min: number; max: number; gain: number } | null>(null);

  useEffect(() => {
    let active = true;
    setProfile(null);
    fetch(stage.gpx)
      .then((response) => response.text())
      .then((text) => {
        const document = new DOMParser().parseFromString(text, "application/xml");
        const values = [...document.querySelectorAll("trkpt ele")]
          .map((node) => Number(node.textContent))
          .filter(Number.isFinite);
        if (!values.length || !active) return;
        const smoothed = values.map((_, index) => {
          const from = Math.max(0, index - 3);
          const to = Math.min(values.length - 1, index + 3);
          let total = 0;
          for (let cursor = from; cursor <= to; cursor += 1) total += values[cursor];
          return total / (to - from + 1);
        });
        const gain = smoothed.slice(1).reduce((total, value, index) => total + Math.max(0, value - smoothed[index]), 0);
        const stride = Math.max(1, Math.ceil(smoothed.length / 160));
        const sampled = smoothed.filter((_, index) => index % stride === 0);
        if (sampled.at(-1) !== smoothed.at(-1)) sampled.push(smoothed.at(-1)!);
        setProfile({ values: sampled, min: Math.round(Math.min(...values)), max: Math.round(Math.max(...values)), gain: Math.round(gain) });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [stage.gpx]);

  if (!profile) return <div className="elevation-loading">Lecture du GPX de l’étape…</div>;
  const width = 720;
  const height = 170;
  const spread = Math.max(1, profile.max - profile.min);
  const points = profile.values.map((value, index) => {
    const x = (index / Math.max(1, profile.values.length - 1)) * width;
    const y = height - 18 - ((value - profile.min) / spread) * (height - 42);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const area = `0,${height} ${points} ${width},${height}`;

  return (
    <div className="elevation-card" aria-label={`Profil altimétrique de ${stage.from} à ${stage.to}`}>
      <div className="elevation-stats">
        <span><small>D+</small><strong>{profile.gain} m</strong></span>
        <span><small>Point bas</small><strong>{profile.min} m</strong></span>
        <span><small>Point haut</small><strong>{profile.max} m</strong></span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        <defs>
          <linearGradient id={`elevation-${stage.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--saffron)" stopOpacity=".62" />
            <stop offset="1" stopColor="var(--saffron)" stopOpacity=".06" />
          </linearGradient>
        </defs>
        <line x1="0" x2={width} y1={height - 1} y2={height - 1} className="elevation-baseline" />
        <polygon points={area} fill={`url(#elevation-${stage.id})`} />
        <polyline points={points} className="elevation-line" />
      </svg>
      <div className="elevation-ends"><span>{stage.from}</span><span>{stage.to}</span></div>
    </div>
  );
}

function MapView(props: {
  route: RouteData | null; resources: OsmResource[]; position: Position | null;
  selectedStage: number; onStage: (stage: number) => void; filters: ResourceCategory[];
  onFilters: (filters: ResourceCategory[]) => void; onResource: (resource: OsmResource) => void;
  onLocate: () => void; onStopLocate: () => void; gpsMessage: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const stageMarkersRef = useRef<Marker[]>([]);
  const resourcesRef = useRef(props.resources);
  const onResourceRef = useRef(props.onResource);
  const [mode, setMode] = useState<"explore" | "follow">("explore");
  const [mapReady, setMapReady] = useState(false);

  resourcesRef.current = props.resources;
  onResourceRef.current = props.onResource;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      center: [1.15, 43.1],
      zoom: 6.2,
      minZoom: 5,
      maxZoom: 18,
      attributionControl: false,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            maxzoom: 19,
            attribution: "© OpenStreetMap",
          },
        },
        layers: [
          { id: "offline-background", type: "background", paint: { "background-color": "#e9eadf" } },
          { id: "osm-base", type: "raster", source: "osm", paint: { "raster-saturation": -0.52, "raster-contrast": 0.06, "raster-brightness-max": 0.93 } },
        ],
      },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");
    map.on("load", () => setMapReady(true));
    const leaveFollow = (event: maplibregl.MapLibreEvent & { originalEvent?: Event }) => {
      if (event.originalEvent) {
        setMode("explore");
        props.onStopLocate();
      }
    };
    map.on("dragstart", leaveFollow);
    map.on("zoomstart", leaveFollow);
    return () => {
      markerRef.current?.remove();
      stageMarkersRef.current.forEach((marker) => marker.remove());
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const mapResources = useMemo(() => props.resources
    .filter((resource) => props.filters.length === 0 || props.filters.includes(resource.category)), [props.resources, props.filters]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !props.route) return;
    const routeData = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: props.route.points.map((point) => [point.lon, point.lat]) },
    } as const;
    const boundaryData = {
      type: "FeatureCollection",
      features: props.route.stageBoundaryPoints.map((point, index) => ({
        type: "Feature",
        properties: { number: index === 13 ? "M" : String(index + 1), active: index === props.selectedStage - 1 ? 1 : 0 },
        geometry: { type: "Point", coordinates: [point.lon, point.lat] },
      })),
    } as const;
    const resourceData = {
      type: "FeatureCollection",
      features: mapResources.map((resource) => ({
        type: "Feature",
        properties: { id: resource.id, name: resource.name, category: resource.category },
        geometry: { type: "Point", coordinates: [resource.lon, resource.lat] },
      })),
    } as const;

    if (!map.getSource("velosud-route")) {
      map.addSource("velosud-route", { type: "geojson", data: routeData });
      map.addLayer({ id: "route-halo", type: "line", source: "velosud-route", paint: { "line-color": "#fffdf4", "line-width": ["interpolate", ["linear"], ["zoom"], 5, 6, 13, 11], "line-opacity": .95 } });
      map.addLayer({ id: "route-line", type: "line", source: "velosud-route", paint: { "line-color": "#d94f35", "line-width": ["interpolate", ["linear"], ["zoom"], 5, 3.5, 13, 6.5] }, layout: { "line-cap": "round", "line-join": "round" } });
      map.addSource("stage-boundaries", { type: "geojson", data: boundaryData });
      map.addLayer({ id: "stage-dots", type: "circle", source: "stage-boundaries", paint: { "circle-radius": ["case", ["==", ["get", "active"], 1], 12, 9], "circle-color": ["case", ["==", ["get", "active"], 1], "#f0ae22", "#183f35"], "circle-stroke-width": 3, "circle-stroke-color": "#fffdf4" } });
      map.addSource("travel-resources", { type: "geojson", data: resourceData, cluster: true, clusterMaxZoom: 13, clusterRadius: 54 });
      map.addLayer({ id: "resource-clusters", type: "circle", source: "travel-resources", filter: ["has", "point_count"], paint: { "circle-color": "#176b87", "circle-opacity": .88, "circle-radius": ["step", ["get", "point_count"], 11, 25, 14, 100, 18], "circle-stroke-width": 2.5, "circle-stroke-color": "#fffdf4" } });
      map.addLayer({
        id: "resource-points", type: "circle", source: "travel-resources", filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 7, 4.5, 14, 8],
          "circle-color": ["match", ["get", "category"], "water", "#1584a4", "food", "#d28421", "sleep", "#795d91", "repair", "#2f755e", "train", "#385392", "health", "#c94e3b", "#66777a"],
          "circle-stroke-width": 2, "circle-stroke-color": "#fffdf4",
        },
      });
      map.on("click", "resource-clusters", async (event) => {
        const feature = event.features?.[0];
        if (!feature || feature.geometry.type !== "Point") return;
        const source = map.getSource("travel-resources") as GeoJSONSource;
        const zoom = await source.getClusterExpansionZoom(Number(feature.properties?.cluster_id));
        map.easeTo({ center: feature.geometry.coordinates as [number, number], zoom });
      });
      map.on("click", "resource-points", (event) => {
        const id = String(event.features?.[0]?.properties?.id ?? "");
        const resource = resourcesRef.current.find((item) => item.id === id);
        if (resource) onResourceRef.current(resource);
      });
      ["resource-clusters", "resource-points"].forEach((layer) => {
        map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
      });
    } else {
      (map.getSource("velosud-route") as GeoJSONSource).setData(routeData);
      (map.getSource("stage-boundaries") as GeoJSONSource).setData(boundaryData);
      (map.getSource("travel-resources") as GeoJSONSource).setData(resourceData);
    }
  }, [mapReady, props.route, props.selectedStage, mapResources]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !props.route) return;
    stageMarkersRef.current.forEach((marker) => marker.remove());
    stageMarkersRef.current = props.route.stageBoundaryPoints.map((point, index) => {
      const element = document.createElement("button");
      element.className = `stage-map-marker ${index === props.selectedStage - 1 ? "active" : ""}`;
      element.textContent = index === 13 ? "M" : String(index + 1);
      element.title = index === 13 ? "Méditerranée" : `Étape ${index + 1}`;
      element.addEventListener("click", () => {
        if (index < 13) props.onStage(index + 1);
      });
      return new maplibregl.Marker({ element }).setLngLat([point.lon, point.lat]).addTo(map);
    });
  }, [mapReady, props.route, props.selectedStage]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !props.route) return;
    const start = props.route.stageBoundaryPoints[Math.max(0, props.selectedStage - 1)];
    const end = props.route.stageBoundaryPoints[Math.min(13, props.selectedStage)];
    if (start && end && mode === "explore") {
      map.fitBounds([[Math.min(start.lon, end.lon), Math.min(start.lat, end.lat)], [Math.max(start.lon, end.lon), Math.max(start.lat, end.lat)]], { padding: 56, duration: 650, maxZoom: 11 });
    }
  }, [props.selectedStage, props.route, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !props.position) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    if (!markerRef.current) {
      const element = document.createElement("div");
      element.className = "gps-marker";
      element.setAttribute("aria-label", "Votre position");
      markerRef.current = new maplibregl.Marker({ element }).setLngLat([props.position.lon, props.position.lat]).addTo(map);
    } else {
      markerRef.current.setLngLat([props.position.lon, props.position.lat]);
    }
    if (mode === "follow") map.easeTo({ center: [props.position.lon, props.position.lat], zoom: Math.max(map.getZoom(), 13), duration: 700 });
  }, [props.position, mode, mapReady]);

  useEffect(() => {
    if (mode === "follow" && /refusée|introuvable|indisponible/i.test(props.gpsMessage)) {
      setMode("explore");
    }
  }, [props.gpsMessage, mode]);

  function explore() {
    setMode("explore");
    props.onStopLocate();
  }

  function follow() {
    setMode("follow");
    props.onLocate();
    if (props.position) mapRef.current?.easeTo({ center: [props.position.lon, props.position.lat], zoom: Math.max(mapRef.current.getZoom(), 13) });
  }

  return (
    <div className="view map-view">
      <header className="map-heading">
        <div><span>ATLANTIQUE → MÉDITERRANÉE</span><h1>La traversée</h1></div>
        <a href="/la-velosud.gpx" download aria-label="Télécharger le GPX complet">GPX ↓</a>
      </header>
      <div className="map-mode-switch" role="group" aria-label="Mode de carte">
        <button className={mode === "explore" ? "active" : ""} onClick={explore}><span>↔</span> Explorer le parcours</button>
        <button className={mode === "follow" ? "active" : ""} onClick={follow}><span>◎</span> Suivre ma position</button>
      </div>
      <div className="stage-picker map-stage-picker">
        <button onClick={() => props.onStage(Math.max(1, props.selectedStage - 1))} aria-label="Étape précédente">←</button>
        <select value={props.selectedStage} onChange={(event) => props.onStage(Number(event.target.value))} aria-label="Étape affichée">
          {stages.map((stage) => <option key={stage.id} value={stage.id}>Étape {stage.id} · {stage.from}–{stage.to}</option>)}
        </select>
        <button onClick={() => props.onStage(Math.min(13, props.selectedStage + 1))} aria-label="Étape suivante">→</button>
      </div>
      <div className="map-frame">
        <div ref={containerRef} className="maplibre-canvas" aria-label="Carte interactive OpenStreetMap de la Vélosud" />
        {!mapReady && <div className="map-loading">Préparation de la carte…</div>}
        <div className={`map-mode-badge ${mode}`}>
          <span>{mode === "follow" ? "◎" : "↔"}</span>
          <div><strong>{mode === "follow" ? "Suivi actif" : "Exploration libre"}</strong><small>{mode === "follow" ? props.gpsMessage : "Le GPS n’est pas utilisé"}</small></div>
        </div>
        {mode === "explore" && <button className="resume-follow" onClick={follow}>◎ Reprendre le suivi</button>}
      </div>
      <FilterRail filters={props.filters} onFilters={props.onFilters} />
      <div className="map-note">
        <strong>{navigator.onLine ? "Fond OSM en ligne" : "Mode hors ligne"}</strong>
        <p>Trace, étapes et points restent disponibles sans réseau. Les tuiles OSM ne sont pas préchargées ; utilisez Organic Maps ou OsmAnd pour un fond détaillé hors ligne.</p>
        <a href="/guide-hors-ligne.md" download>Guide hors-ligne ↓</a>
      </div>
      <p className="osm-credit">Fond © contributeurs OpenStreetMap · données ODbL 1.0</p>
    </div>
  );
}

function FilterRail({ filters, onFilters }: { filters: ResourceCategory[]; onFilters: (filters: ResourceCategory[]) => void }) {
  const principal: ResourceCategory[] = ["water", "food", "sleep", "repair", "train", "health"];
  return (
    <div className="filter-rail" aria-label="Filtres">
      {principal.map((category) => (
        <button
          key={category}
          className={filters.includes(category) ? "active" : ""}
          onClick={() => onFilters(filters.includes(category) ? filters.filter((item) => item !== category) : [...filters, category])}
        >
          <span className={`category-mark cat-${category}`}>{categoryLabels[category].icon}</span>{categoryLabels[category].label}
        </button>
      ))}
      {filters.length > 0 && <button className="clear-filter" onClick={() => onFilters([])}>Tout</button>}
    </div>
  );
}

function StagesView({ current, selected, onSelect }: { current: number; selected: number; onSelect: (stage: Stage) => void }) {
  return (
    <div className="view stages-view">
      <header className="page-title"><span>BIARRITZ → LE BARCARÈS</span><h1>13 étapes, rythme libre</h1><p>Les étapes officielles sont des repères : fractionnez ou regroupez selon chaleur, terrain et accueil.</p></header>
      <div className="stage-summary"><strong>≈ 650 km</strong><span>36 % voies cyclables</span><span>Point haut 669 m</span></div>
      <div className="stage-timeline">
        {stages.map((stage) => (
          <button key={stage.id} className={`${stage.id === current ? "current" : ""} ${stage.id === selected ? "selected" : ""}`} onClick={() => onSelect(stage)}>
            <span className="timeline-number">{String(stage.id).padStart(2, "0")}</span>
            <span className="timeline-line"><i /></span>
            <span className="timeline-copy">
              <small>{stage.difficulty === "aventure" ? "ÉTAPE SENSIBLE" : stage.officialTime}</small>
              <strong>{stage.from} <em>→</em> {stage.to}</strong>
              <span>{stage.distanceKm} km · {stage.terrain}</span>
              {stage.watch.length > 2 && <b>{stage.watch[0]}</b>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ResourcesView(props: {
  items: { resource: OsmResource; distance: number }[]; count: number; generatedAt: string;
  search: string; onSearch: (value: string) => void; filters: ResourceCategory[];
  onFilters: (filters: ResourceCategory[]) => void; stage: number; onStage: (stage: number) => void;
  favoritesOnly: boolean; onFavorites: (value: boolean) => void; favoriteIds: string[];
  states: Record<string, string>; onSelect: (resource: OsmResource) => void;
}) {
  return (
    <div className="view resources-view">
      <header className="page-title"><span>DONNÉES OUVERTES</span><h1>{props.count.toLocaleString("fr-FR")} points utiles</h1><p>Eau, nourriture, gares, santé et réparation filtrés autour de la trace.</p></header>
      <div className="search-box">
        <span>⌕</span><input value={props.search} onChange={(event) => props.onSearch(event.target.value)} placeholder="Rechercher un lieu ou un service" />
      </div>
      <div className="resource-controls">
        <select value={props.stage} onChange={(event) => props.onStage(Number(event.target.value))}>
          <option value={0}>Toutes les étapes</option>
          {stages.map((stage) => <option key={stage.id} value={stage.id}>Étape {stage.id} · {stage.to}</option>)}
        </select>
        <button className={props.favoritesOnly ? "active" : ""} onClick={() => props.onFavorites(!props.favoritesOnly)}>★ Favoris</button>
      </div>
      <FilterRail filters={props.filters} onFilters={props.onFilters} />
      <div className="resource-list">
        {props.items.map(({ resource, distance }) => (
          <button key={resource.id} onClick={() => props.onSelect(resource)} className="resource-row">
            <span className={`category-mark cat-${resource.category}`}>{categoryLabels[resource.category].icon}</span>
            <span className="resource-copy">
              <strong>{resource.name}{props.favoriteIds.includes(resource.id) && <em>★</em>}</strong>
              <small>{categoryLabels[resource.category].label} · étape {resource.stageId} · {resource.openingHours || "horaires à vérifier"}</small>
              {props.states[resource.id] && props.states[resource.id] !== "à vérifier" && <b>{props.states[resource.id]}</b>}
            </span>
            <span className="resource-distance"><strong>{formatDistance(distance)}</strong><small>{resource.distanceToRouteKm ? `${formatDistance(resource.distanceToRouteKm)} trace` : "sur trace"}</small></span>
          </button>
        ))}
        {props.items.length === 0 && <p className="empty">Aucune ressource pour ces filtres. Élargissez l’étape ou retirez un filtre.</p>}
      </div>
      <footer className="data-footer">
        <strong>OpenStreetMap · ODbL 1.0</strong>
        <span>Collecte du {props.generatedAt ? new Date(props.generatedAt).toLocaleDateString("fr-FR") : "28/07/2026"}. Vérifiez l’ouverture et la potabilité sur place.</span>
      </footer>
    </div>
  );
}

function SafetyView(props: {
  pane: "risks" | "trains" | "sleep" | "emergency";
  onPane: (pane: "risks" | "trains" | "sleep" | "emergency") => void;
  lastCheck: string | null; onChecked: () => void;
}) {
  return (
    <div className="view safety-view">
      <header className="page-title danger-title"><span>DÉCIDER AVANT DE PARTIR</span><h1>Sécurité & replis</h1><p>Les informations dynamiques ne sont valables que si leur heure est récente.</p></header>
      <div className="safety-tabs">
        {[
          ["risks", "Feux & météo"], ["trains", "Trains"], ["sleep", "Dormir"], ["emergency", "Urgence"],
        ].map(([id, label]) => <button key={id} className={props.pane === id ? "active" : ""} onClick={() => props.onPane(id as typeof props.pane)}>{label}</button>)}
      </div>
      {props.pane === "risks" && (
        <>
          <div className="freshness-card">
            <span className={props.lastCheck ? "fresh" : ""}>●</span>
            <div><strong>{whenLabel(props.lastCheck)}</strong><small>Ouvrez les sources correspondant à l’étape, puis marquez le contrôle.</small></div>
            <button onClick={props.onChecked}>Marquer vérifié</button>
          </div>
          <div className="official-list">
            {safetySources.map((source) => (
              <a key={source.id} href={source.url} target="_blank" rel="noreferrer" className={source.critical ? "critical" : ""}>
                <span>{source.critical ? "!" : "i"}</span>
                <div><strong>{source.label}</strong><small>{source.area} · {source.cadence}</small></div>
                <b>↗</b>
              </a>
            ))}
          </div>
          <div className="stove-rule"><strong>Règle réchaud</strong><p>Pas de flamme si une interdiction, du vent fort ou un doute existe. L’autorisation du terrain ne remplace jamais l’arrêté préfectoral.</p></div>
        </>
      )}
      {props.pane === "trains" && (
        <>
          <section className="ticket-plan">
            <span>BILLETS À ACHETER</span>
            <h2>Lyon → côte basque</h2>
            <p><strong>8 août</strong> en premier choix, <strong>9 août</strong> en repli. Chercher deux vélos non démontés sur chaque train.</p>
            <ol>
              <li><b>Option prioritaire</b><span>Lyon → Toulouse, puis Intercités Toulouse → Bayonne/Biarritz. Réserver chaque vélo en même temps que chaque billet.</span></li>
              <li><b>Option de repli</b><span>TER via Nîmes/Montpellier puis Toulouse, uniquement avec réservations régionales et correspondances généreuses.</span></li>
              <li><b>Retour</b><span>Le Barcarès → Rivesaltes/Perpignan à vélo, puis Perpignan → Lyon avec deux places vélo confirmées.</span></li>
            </ol>
            <a href="https://www.sncf-connect.com/" target="_blank" rel="noreferrer">Rechercher sur SNCF Connect ↗</a>
          </section>
          <div className="rule-list">
            {trainRules.map((item) => <a key={item.title} href={item.url} target="_blank" rel="noreferrer"><strong>{item.title}</strong><span>{item.rule}</span><b>Source ↗</b></a>)}
          </div>
          <p className="warning-copy">Ne jamais considérer un trajet compatible tant que les deux emplacements vélo n’apparaissent pas dans la réservation. Prévoir au moins 25 minutes pour une correspondance avec vélos chargés.</p>
        </>
      )}
      {props.pane === "sleep" && (
        <>
          <div className="legal-card"><span>RÈGLE DE BASE</span><h2>Demander avant de planter</h2><p>Hors de l’emprise des routes et voies publiques, le camping suppose l’accord de la personne qui a la jouissance du sol. Les interdictions locales, réserves et arrêtés incendie restent prioritaires.</p><a href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000031721244/" target="_blank" rel="noreferrer">Article R111-32 ↗</a></div>
          <div className="message-template"><span>MESSAGE À COPIER</span><p>Bonjour, nous traversons les Pyrénées à vélo à deux. Accepteriez-vous que nous installions nos deux petites tentes dans un coin de votre terrain pour une nuit ? Nous repartons tôt, sans feu et sans laisser de trace. Aucun souci si ce n’est pas possible. Merci !</p></div>
          <div className="hospitality-list">{hospitalityLinks.map((item) => <a key={item.name} href={item.url} target="_blank" rel="noreferrer"><strong>{item.name}</strong><span>{item.detail}</span><b>↗</b></a>)}</div>
          <p className="privacy-note">Aucune adresse privée d’hôte n’est enregistrée dans cette application.</p>
        </>
      )}
      {props.pane === "emergency" && (
        <>
          <div className="emergency-numbers">
            <a href="tel:112"><strong>112</strong><span>Urgence européenne</span></a>
            <a href="tel:15"><strong>15</strong><span>SAMU</span></a>
            <a href="tel:18"><strong>18</strong><span>Pompiers</span></a>
            <a href="sms:114"><strong>114</strong><span>Urgence SMS</span></a>
          </div>
          <div className="guide-list">{emergencyGuides.map((guide) => <details key={guide.title}><summary>{guide.title}<b>+</b></summary><p>{guide.body}</p></details>)}</div>
          <a className="download-sheet" href="/urgence.md" download>Télécharger la fiche d’urgence</a>
        </>
      )}
    </div>
  );
}

function TeamView({ travelers }: { travelers: Traveler[] }) {
  return (
    <div className="view team-view">
      <header className="page-title">
        <span>DEUX VÉLOS · UNE TRAVERSÉE</span>
        <h1>L’équipe de route</h1>
        <p>Les informations viennent uniquement des ressources déjà fournies. Le fichier local <code>public/data/travelers.json</code> permet de compléter les profils sans toucher à l’application.</p>
      </header>
      <div className="traveler-grid">
        {travelers.map((traveler) => (
          <article className={`traveler-card ${traveler.status}`} key={traveler.id}>
            <header>
              <span className="traveler-monogram">{traveler.monogram}</span>
              <div><small>{traveler.status === "incomplete" ? "PROFIL À COMPLÉTER" : "PRÊT À ROULER"}</small><h2>{traveler.name}</h2><p>{traveler.tagline}</p></div>
            </header>
            <div className="traveler-facts">
              <section>
                <span>VÉLO</span>
                {traveler.bike ? <a href={traveler.bike.url || undefined} target="_blank" rel="noreferrer"><strong>{traveler.bike.name}</strong>{traveler.bike.url && <b>↗</b>}</a> : <p className="missing-value">À renseigner dans travelers.json</p>}
              </section>
              <section>
                <span>TENTE</span>
                <strong>{traveler.shelter?.name || "À renseigner"}</strong>
              </section>
              <section>
                <span>MATÉRIEL DOCUMENTÉ</span>
                {traveler.gear.length ? (
                  <ul>{traveler.gear.map((item) => <li key={item.name}>{item.url ? <a href={item.url} target="_blank" rel="noreferrer"><strong>{item.name}</strong><small>{item.category} ↗</small></a> : <strong>{item.name}</strong>}</li>)}</ul>
                ) : <p className="missing-value">Aucun matériel fourni pour le moment.</p>}
              </section>
              {traveler.notes.length > 0 && <section><span>NOTE DE VOYAGE</span>{traveler.notes.map((note) => <p key={note}>{note}</p>)}</section>}
            </div>
          </article>
        ))}
        {travelers.length === 0 && <p className="empty">Chargement des profils locaux…</p>}
      </div>
      <p className="privacy-note">Ces profils sont publics avec le lien de l’application : aucune adresse, donnée médicale ou coordonnée privée n’y figure.</p>
    </div>
  );
}

function ResourceSheet(props: {
  resource: OsmResource; favorite: boolean; state: string; note: string;
  onFavorite: () => void; onState: (state: string) => void; onNote: (note: string) => void; onClose: () => void;
}) {
  const resource = props.resource;
  const navUrl = `geo:${resource.lat},${resource.lon}?q=${resource.lat},${resource.lon}(${encodeURIComponent(resource.name)})`;
  return (
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <section className="bottom-sheet" role="dialog" aria-modal="true" aria-label={resource.name}>
        <button className="sheet-close" onClick={props.onClose}>×</button>
        <div className="sheet-handle" />
        <span className={`category-mark large cat-${resource.category}`}>{categoryLabels[resource.category].icon}</span>
        <small>{categoryLabels[resource.category].label} · étape {resource.stageId}</small>
        <h2>{resource.name}</h2>
        <p className="resource-meta">{formatDistance(resource.distanceToRouteKm)} de la trace · {resource.openingHours || "horaires à vérifier"}</p>
        {resource.category === "water_unverified" && <p className="water-warning">Potabilité non confirmée : traiter ou choisir un point explicitement potable.</p>}
        <div className="sheet-actions">
          <a href={navUrl}>◎ Guider</a>
          {resource.phone && <a href={`tel:${resource.phone}`}>☎ Appeler</a>}
          <a href={resource.sourceUrl} target="_blank" rel="noreferrer">Source ↗</a>
          <button className={props.favorite ? "favorite" : ""} onClick={props.onFavorite}>{props.favorite ? "★ Favori" : "☆ Favori"}</button>
        </div>
        <label className="status-field">Statut local
          <select value={props.state} onChange={(event) => props.onState(event.target.value)}>
            <option>à vérifier</option><option>vérifié</option><option>fermé</option><option>utilisé</option>
          </select>
        </label>
        <label className="note-field">Note sur ce téléphone
          <textarea value={props.note} onChange={(event) => props.onNote(event.target.value)} placeholder="Accès, accueil, eau coupée…" />
        </label>
        <p className="source-line">OpenStreetMap · collecté le {resource.verifiedAt} · état à confirmer sur place.</p>
      </section>
    </div>
  );
}

function StageSheet({ stage, onClose }: { stage: Stage; onClose: () => void }) {
  return (
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="bottom-sheet stage-sheet" role="dialog" aria-modal="true">
        <button className="sheet-close" onClick={onClose}>×</button><div className="sheet-handle" />
        <small>ÉTAPE {stage.id} · {stage.difficulty.toUpperCase()}</small>
        <h2>{stage.from} <em>→</em> {stage.to}</h2>
        <div className="stage-sheet-stats"><strong>{stage.distanceKm} km</strong><span>{stage.officialTime}</span></div>
        <p>{stage.summary}</p>
        <h3>Terrain</h3><p>{stage.terrain}</p>
        <h3>À surveiller</h3><ul>{stage.watch.map((item) => <li key={item}>{item}</li>)}</ul>
        <h3>Ravitaillement</h3><p>{stage.stops.join(" · ")}</p>
        <h3>Replis</h3><p>{stage.retreat.join(" · ")}</p>
        <h3>Nuit</h3><p>{stage.sleepPlan}</p>
        <div className="sheet-actions"><a href={stage.gpx} download>Télécharger GPX</a><a href={`geo:0,0?q=${encodeURIComponent(stage.to)}`}>Ouvrir l’arrivée</a></div>
      </section>
    </div>
  );
}
